import { useCallback, useEffect, useState } from 'react';

import { LoadingStatuses } from '@argenta/react-ui';

import {
  ApiResponseError,
  ApiServiceError,
  ApiServiceHookProps,
  ApiServiceHookType,
  ApiServiceType,
} from './useApiService.types';
import { mapErrorToServiceError } from './useApiService.utils';

export const useApiService: ApiServiceHookType = <RequestType, ResponseType>({
  service,
  initialServiceRequestData,
  responseTransformers,
  immediateExecute = true,
}: ApiServiceHookProps<RequestType, ResponseType>) => {
  const [loadingStatus, setLoadingStatus] = useState(LoadingStatuses.IDLE);
  const [error, setError] = useState<ApiServiceError<ApiResponseError>>();
  const [response, setResponse] = useState<ResponseType>();

  const executeCall: ApiServiceType<RequestType, ResponseType> = useCallback(
    async ({ requestData, abortController } = {}) => {
      setLoadingStatus(LoadingStatuses.LOADING);
      setError(undefined);
      setResponse(undefined);

      try {
        let serviceResponse: ResponseType | undefined = await service({
          requestData,
          abortController,
        });

        if (responseTransformers) {
          if (Array.isArray(responseTransformers)) {
            responseTransformers.forEach((transformer) => {
              serviceResponse = transformer(serviceResponse);
            });
          } else {
            serviceResponse = responseTransformers(serviceResponse);
          }
        }

        setLoadingStatus(LoadingStatuses.DONE);
        setResponse(serviceResponse);
        return serviceResponse;
      } catch (catchError) {
        const serviceError = mapErrorToServiceError(catchError);

        if (!abortController?.signal.aborted) {
          setLoadingStatus(LoadingStatuses.ERROR);
          setError(serviceError);
        }

        throw serviceError;
      }
    },
    [service, responseTransformers]
  );

  useEffect(() => {
    let abortController: AbortController;

    if (immediateExecute) {
      abortController = new AbortController();
      executeCall({ requestData: initialServiceRequestData, abortController }).catch(
        () => undefined
      );
    }

    return () => {
      abortController?.abort();
    };
  }, [executeCall, initialServiceRequestData, immediateExecute]);

  return { loadingStatus, error, response, executeCall };
};
